import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import {
  MdAdd,
  MdClose,
  MdDelete,
  MdEditNote,
  MdGridView,
  MdPlace,
  MdRefresh,
  MdTableRows,
} from "react-icons/md";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { can } from "../auth/permissions";
import { commissionPctOf, pctLabel } from "../commission";
import "../new-order.css";
import "../funeral-directors.css";

/* ------------------------------------------------------------------ *
 * Funeral directors (redesign) — /funeral-directors
 *
 * Replaces src/pages/funeral_directors.js (kept at /FuneralDirectors-v1):
 *   - a ten-column strip where every field was squeezed to ~9% of the
 *     width                                 -> one card per director,
 *                                              code-led, every field
 *                                              readable
 *   - an always-open form pinned to a 2/12
 *     sidebar column                        -> an editor drawer opened
 *                                              by Add or Edit
 *   - delete fired on a bare icon click     -> an inline confirm over
 *                                              the card
 *   - `unique_info` was write-only: the form
 *     saved it, the table never showed it   -> shown as a note on the
 *                                              card when set
 *   - a `CalcTimeDifference(delivery_date)`
 *     date separator on records that carry
 *     no date at all                        -> dropped
 *
 * Cards are the default; the toggle switches to a table that keeps the
 * legacy column set, for the density 59 directors sometimes wants. The
 * choice is remembered per browser.
 *
 * Same endpoints as the legacy page: GET /fd/getAll, POST /fd/addNew,
 * POST /fd/updateFD, POST /fd/delete ({ id }). Addresses and casket
 * cover delivery locations stay JSON strings of { name, coordinates }.
 * ------------------------------------------------------------------ */

const EMPTY = {
  business_name: "",
  code: "",
  address: "",
  email_address: "",
  contact_number: "",
  owner_name: "",
  manager_name: "",
  cc_delivery_location: "",
  unique_info: "",
  /* % of the ex-GST order value, applied per work order when ticked */
  commission_pct: 0,
};

const VIEW_KEY = "rf-fd-view";

const safeParse = (raw, fallback) => {
  if (raw === undefined || raw === null || raw === "") return fallback;
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return fallback;
  }
};

const locOf = (raw) => safeParse(raw, {}) || {};

const mapsHref = (loc) =>
  loc.coordinates
    ? `https://www.google.com/maps/search/?api=1&query=${loc.coordinates.lat},${loc.coordinates.lng}`
    : null;

/* A location cell: a map link when the place was picked from Google and
   carries coordinates, plain text when it was only typed. */
function LocationValue({ raw }) {
  const loc = locOf(raw);
  const name = loc.name || (typeof raw === "string" && raw[0] !== "{" ? raw : "");
  if (!name) return <span className="fdr-row-v is-empty">Not set</span>;
  const href = mapsHref(loc);
  return (
    <span className="fdr-row-v">
      {href ? (
        <a href={href} target="_blank" rel="noreferrer">
          <MdPlace className="fdr-pin" />
          {name}
        </a>
      ) : (
        name
      )}
    </span>
  );
}

/* The same location, as a table cell rather than a card row. */
function LocationCell({ raw }) {
  const loc = locOf(raw);
  const name = loc.name || (typeof raw === "string" && raw[0] !== "{" ? raw : "");
  if (!name) return <span className="fdr-t-empty">—</span>;
  const href = mapsHref(loc);
  return href ? (
    <a href={href} target="_blank" rel="noreferrer">
      {name}
    </a>
  ) : (
    <>{name}</>
  );
}

function TextCell({ value, href }) {
  if (!value) return <span className="fdr-t-empty">—</span>;
  return href ? (
    <a href={href + value} target="_blank" rel="noreferrer">
      {value}
    </a>
  ) : (
    <>{value}</>
  );
}

/* One labelled line inside a merged table cell. */
function Line({ k, nowrap, children }) {
  return (
    <span className="fdr-t-line">
      <span className="fdr-t-lk">{k}</span>
      <span className={`fdr-t-lv${nowrap ? " is-nowrap" : ""}`}>{children}</span>
    </span>
  );
}

function Row({ k, children }) {
  return (
    <div className="fdr-row">
      <span className="fdr-row-k">{k}</span>
      {children}
    </div>
  );
}

function TextValue({ value, href, empty }) {
  if (!value) return <span className="fdr-row-v is-empty">{empty}</span>;
  return (
    <span className="fdr-row-v">
      {href ? (
        <a href={href + value} target="_blank" rel="noreferrer">
          {value}
        </a>
      ) : (
        value
      )}
    </span>
  );
}

export function FuneralDirectorsRedesign() {
  const { user } = useAuth() || {};
  const canWrite = can(user, "fd:write");

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState("");
  /* Cards by default; the stored choice only overrides it when it is
     the other valid value, and reading it may throw in a private
     window, so it is guarded. */
  const [view, setView] = useState(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === "table" ? "table" : "cards";
    } catch (e) {
      return "cards";
    }
  });
  const [editing, setEditing] = useState(null); // { …fd } or EMPTY for new
  const [confirming, setConfirming] = useState(null); // fd id
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    axios
      .get(BASE_SERVER_URL + "/fd/getAll")
      .then((res) => {
        setList(res.data.response || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadError(true);
      });
  }, []);

  useEffect(refresh, [refresh]);

  /* /fd/getAll returns every director, so filtering stays on the client
     — no endpoint round trip per keystroke. */
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((fd) =>
      [
        fd.business_name,
        fd.code,
        fd.email_address,
        fd.contact_number,
        fd.owner_name,
        fd.manager_name,
        locOf(fd.address).name,
        locOf(fd.cc_delivery_location).name,
        fd.unique_info,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().indexOf(q) !== -1)
    );
  }, [list, search]);

  function pickView(next) {
    setView(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch (e) {
      /* private window or blocked storage — the choice just won't stick */
    }
  }

  function remove(id) {
    setDeleting(true);
    axios
      .post(BASE_SERVER_URL + "/fd/delete", { id })
      .then(() => {
        setDeleting(false);
        setConfirming(null);
        toast.success("Funeral director deleted.");
        refresh();
      })
      .catch(() => {
        setDeleting(false);
        toast.error("Couldn't delete that director.");
      });
  }

  return (
    <div className="fdr-page">
      {/* ---------------- toolbar ---------------- */}
      <div className="fdr-bar">
        <label className="fdr-search">
          <span className="fdr-search-icon" />
          <input
            type="search"
            value={search}
            placeholder="Search by name, code, contact or location"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <span className="fdr-count rf-num">
          {loading
            ? "…"
            : `${shown.length}${
                shown.length === list.length ? "" : ` of ${list.length}`
              } director${shown.length === 1 ? "" : "s"}`}
        </span>

        <span className="rf-spacer" />

        <div className="rf-segmented fdr-view">
          <button
            type="button"
            className={view === "cards" ? "is-active" : ""}
            onClick={() => pickView("cards")}
            title="Card view"
            aria-pressed={view === "cards"}
          >
            <MdGridView /> Cards
          </button>
          <button
            type="button"
            className={view === "table" ? "is-active" : ""}
            onClick={() => pickView("table")}
            title="Table view"
            aria-pressed={view === "table"}
          >
            <MdTableRows /> Table
          </button>
        </div>

        <button
          type="button"
          className="rf-btn rf-btn-ghost"
          onClick={refresh}
          title="Reload"
        >
          <MdRefresh style={{ verticalAlign: "-2px" }} /> Reload
        </button>
        {canWrite && (
          <button
            type="button"
            className="rf-btn rf-btn-primary"
            onClick={() => setEditing({ ...EMPTY })}
          >
            <MdAdd style={{ verticalAlign: "-2px" }} /> Add director
          </button>
        )}
      </div>

      {/* ---------------- grid ---------------- */}
      <div className="fdr-body">
        {loading && <div className="rf-empty">Loading funeral directors…</div>}

        {!loading && loadError && (
          <div className="rf-empty">
            Couldn&apos;t load funeral directors.{" "}
            <button type="button" className="rf-btn rf-btn-ghost" onClick={refresh}>
              Retry
            </button>
          </div>
        )}

        {!loading && !loadError && shown.length === 0 && (
          <div className="rf-empty">
            {list.length === 0
              ? "No funeral directors yet."
              : `No director matches “${search.trim()}”.`}
          </div>
        )}

        {!loading && !loadError && shown.length > 0 && view === "table" && (
          <div className="fdr-tablewrap">
            <div className="fdr-scroll">
              <table className="fdr-table">
                <thead>
                  <tr>
                    <th className="fdr-t-num">#</th>
                    <th>Code</th>
                    <th>Business name</th>
                    <th>Locations</th>
                    <th>Contact</th>
                    <th>People</th>
                    <th className="fdr-t-pct">Commission</th>
                    {canWrite && <th className="fdr-t-acts" />}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((fd, i) => (
                    <tr
                      key={fd._id}
                      className={confirming === fd._id ? "is-confirming" : ""}
                    >
                      <td className="fdr-t-num">{i + 1}</td>
                      <td className={`fdr-t-code${fd.code ? "" : " is-empty"}`}>
                        {fd.code || "No code"}
                      </td>
                      <td className="fdr-t-name">
                        <b>{fd.business_name || "Unnamed director"}</b>
                        {fd.unique_info && (
                          <span className="fdr-t-note" title={fd.unique_info}>
                            {fd.unique_info}
                          </span>
                        )}
                      </td>
                      <td className="fdr-t-pair is-loc">
                        <Line k="Address">
                          <LocationCell raw={fd.address} />
                        </Line>
                        <Line k="C/C to">
                          <LocationCell raw={fd.cc_delivery_location} />
                        </Line>
                      </td>
                      <td className="fdr-t-pair is-contact">
                        <Line k="Email">
                          <TextCell value={fd.email_address} href="mailto:" />
                        </Line>
                        <Line k="Phone" nowrap>
                          <TextCell value={fd.contact_number} href="tel:" />
                        </Line>
                      </td>
                      <td className="fdr-t-pair is-people">
                        <Line k="Owner">
                          {fd.owner_name || <span className="fdr-t-empty">—</span>}
                        </Line>
                        <Line k="Manager">
                          {fd.manager_name || <span className="fdr-t-empty">—</span>}
                        </Line>
                      </td>
                      <td className="fdr-t-pct">
                        {Number(fd.commission_pct) > 0 ? (
                          pctLabel(fd.commission_pct)
                        ) : (
                          <span className="fdr-t-empty">—</span>
                        )}
                      </td>
                      {canWrite && (
                        <td className="fdr-t-acts">
                          {confirming === fd._id ? (
                            <span className="fdr-t-confirm">
                              <span className="fdr-t-confirm-text">Delete?</span>
                              <button
                                type="button"
                                className="rf-btn rf-btn-ghost"
                                onClick={() => setConfirming(null)}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                className="rf-btn fdr-btn-danger"
                                onClick={() => remove(fd._id)}
                                disabled={deleting}
                              >
                                {deleting ? "Deleting…" : "Delete"}
                              </button>
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="fdr-mini"
                                title="Edit this director"
                                onClick={() => setEditing(fd)}
                              >
                                <MdEditNote />
                              </button>
                              <button
                                type="button"
                                className="fdr-mini is-danger"
                                title="Delete this director"
                                onClick={() => setConfirming(fd._id)}
                              >
                                <MdDelete />
                              </button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!loading && !loadError && shown.length > 0 && view === "cards" && (
          <div className="fdr-grid">
            {shown.map((fd) => {
              const people = [
                fd.owner_name && `Owner ${fd.owner_name}`,
                fd.manager_name && `Manager ${fd.manager_name}`,
              ].filter(Boolean);
              return (
                <article className="fdr-card" key={fd._id}>
                  {confirming === fd._id && (
                    <div className="fdr-confirm">
                      <div className="fdr-confirm-text">
                        Delete <b>{fd.business_name || "this director"}</b>?
                        <br />
                        Work orders that name them keep their own copy.
                      </div>
                      <div className="fdr-confirm-acts">
                        <button
                          type="button"
                          className="rf-btn rf-btn-ghost"
                          onClick={() => setConfirming(null)}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="rf-btn fdr-btn-danger"
                          onClick={() => remove(fd._id)}
                          disabled={deleting}
                        >
                          {deleting ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="fdr-head">
                    <div className="fdr-head-main">
                      <div className="fdr-name">
                        {fd.business_name || "Unnamed director"}
                      </div>
                      {people.length > 0 && (
                        <div className="fdr-people">{people.join(" · ")}</div>
                      )}
                    </div>
                    {canWrite && (
                      <div className="fdr-acts">
                        <button
                          type="button"
                          className="fdr-mini"
                          title="Edit this director"
                          onClick={() => setEditing(fd)}
                        >
                          <MdEditNote />
                        </button>
                        <button
                          type="button"
                          className="fdr-mini is-danger"
                          title="Delete this director"
                          onClick={() => setConfirming(fd._id)}
                        >
                          <MdDelete />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="fdr-rows">
                    <Row k="Address">
                      <LocationValue raw={fd.address} />
                    </Row>
                    <Row k="C/C to">
                      <LocationValue raw={fd.cc_delivery_location} />
                    </Row>
                    <Row k="Email">
                      <TextValue
                        value={fd.email_address}
                        href="mailto:"
                        empty="No email"
                      />
                    </Row>
                    <Row k="Phone">
                      <TextValue
                        value={fd.contact_number}
                        href="tel:"
                        empty="No number"
                      />
                    </Row>
                    <Row k="Commission">
                      {Number(fd.commission_pct) > 0 ? (
                        <span className="fdr-row-v">
                          {pctLabel(fd.commission_pct)} of the ex-GST order value
                        </span>
                      ) : (
                        <span className="fdr-row-v is-empty">None</span>
                      )}
                    </Row>
                  </div>

                  {/* the legacy form saved this and the table never showed it */}
                  {fd.unique_info && (
                    <div className="fdr-note">
                      <span className="fdr-note-k">Special information</span>
                      {fd.unique_info}
                    </div>
                  )}

                  <div className="fdr-foot">
                    <span className={`fdr-code${fd.code ? "" : " is-empty"}`}>
                      {fd.code || "No code"}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <DirectorEditor
          director={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * DirectorEditor — the add/edit drawer.
 *
 * Locations go through react-places-autocomplete, the same library the
 * legacy LocationInputField used, and are stored the same way: the typed
 * name alone until a suggestion is picked, then { name, coordinates }.
 * ------------------------------------------------------------------ */
function DirectorEditor({ director, onClose, onSaved }) {
  const isEdit = Boolean(director._id);
  const [form, setForm] = useState({ ...EMPTY, ...director });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const set = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const nameMissing = !String(form.business_name || "").trim();
  const pct = commissionPctOf(form.commission_pct);
  const pctInvalid = pct === null;

  function save() {
    if (nameMissing) {
      toast.error("A business name is required.");
      return;
    }
    if (pctInvalid) {
      toast.error("Commission must be a number between 0 and 100.");
      return;
    }
    setSaving(true);
    const url = isEdit
      ? BASE_SERVER_URL + "/fd/updateFD"
      : BASE_SERVER_URL + "/fd/addNew";
    /* The server wants the rate as a number; the input holds a string. */
    axios
      .post(url, { ...form, commission_pct: pct })
      .then(() => {
        setSaving(false);
        toast.success(isEdit ? "Director updated." : "Director added.");
        onSaved();
      })
      .catch((err) => {
        setSaving(false);
        /* a bad rate comes back as a 400 with a human-readable message */
        const message = err && err.response && err.response.data && err.response.data.message;
        toast.error(message || "Couldn't save that director.");
      });
  }

  return (
    <div className="fde-wrap" role="dialog" aria-modal="true">
      <div className="fde-scrim" onClick={onClose} />
      <div className="fde-panel">
        <header className="fde-head">
          <div className="fde-head-main">
            <div className="fde-eyebrow">
              {isEdit ? "Editing director" : "New director"}
            </div>
            <h3 className="fde-title">
              {form.business_name || "Funeral director"}
            </h3>
          </div>
          <button type="button" className="fde-close" onClick={onClose} title="Close">
            <MdClose />
          </button>
        </header>

        <div className="fde-body">
          <div className="fde-sec">Identity</div>
          <div className="fde-fields">
            <label className="rf-field">
              <span className="rf-label">Business name</span>
              <span className={`rf-input-shell${nameMissing ? " is-error" : ""}`}>
                <input
                  value={form.business_name}
                  placeholder="Tobin Brothers Funerals"
                  onChange={(e) => set("business_name", e.target.value)}
                />
              </span>
            </label>
            <label className="rf-field">
              <span className="rf-label">Code</span>
              <span className="rf-input-shell">
                <input
                  value={form.code}
                  placeholder="TB — how run sheets refer to them"
                  onChange={(e) => set("code", e.target.value)}
                />
              </span>
            </label>
          </div>

          <div className="fde-sec">Commission</div>
          <div className="fde-fields">
            <label className="rf-field">
              <span className="rf-label">Rate (% of the ex-GST order value)</span>
              <span className={`rf-input-shell fde-pct${pctInvalid ? " is-error" : ""}`}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  inputMode="decimal"
                  value={
                    form.commission_pct === undefined || form.commission_pct === null
                      ? ""
                      : form.commission_pct
                  }
                  placeholder="0"
                  onChange={(e) => set("commission_pct", e.target.value)}
                />
                <span className="fde-pct-unit">%</span>
              </span>
              <span className="fde-help">
                {pctInvalid
                  ? "Enter a number from 0 to 100."
                  : "Charged on a funeral work order only when “Apply funeral director commission” is ticked on it. Each order keeps the rate it was ticked at."}
              </span>
            </label>
          </div>

          <div className="fde-sec">Locations</div>
          <div className="fde-fields">
            <PlacesField
              label="Address"
              placeholder="Search for the business address"
              value={form.address}
              onChange={(v) => set("address", v)}
            />
            <PlacesField
              label="Casket cover delivery location"
              placeholder="Where casket covers go by default"
              value={form.cc_delivery_location}
              onChange={(v) => set("cc_delivery_location", v)}
            />
          </div>

          <div className="fde-sec">Contact</div>
          <div className="fde-fields">
            <div className="rf-grid-2">
              <label className="rf-field">
                <span className="rf-label">Email</span>
                <span className="rf-input-shell">
                  <input
                    type="email"
                    value={form.email_address}
                    placeholder="orders@example.com"
                    onChange={(e) => set("email_address", e.target.value)}
                  />
                </span>
              </label>
              <label className="rf-field">
                <span className="rf-label">Phone</span>
                <span className="rf-input-shell">
                  <input
                    value={form.contact_number}
                    placeholder="03 9000 0000"
                    onChange={(e) => set("contact_number", e.target.value)}
                  />
                </span>
              </label>
            </div>
            <div className="rf-grid-2">
              <label className="rf-field">
                <span className="rf-label">Owner</span>
                <span className="rf-input-shell">
                  <input
                    value={form.owner_name}
                    placeholder="Owner's name"
                    onChange={(e) => set("owner_name", e.target.value)}
                  />
                </span>
              </label>
              <label className="rf-field">
                <span className="rf-label">Manager</span>
                <span className="rf-input-shell">
                  <input
                    value={form.manager_name}
                    placeholder="Manager's name"
                    onChange={(e) => set("manager_name", e.target.value)}
                  />
                </span>
              </label>
            </div>
          </div>

          <div className="fde-sec">Special information</div>
          <label className="rf-field">
            <span className="rf-label">Anything the florist should know</span>
            <span className="rf-input-shell is-area">
              <textarea
                rows={4}
                value={form.unique_info}
                placeholder="Access notes, standing preferences, who to call."
                onChange={(e) => set("unique_info", e.target.value)}
              />
            </span>
          </label>
        </div>

        <div className="fde-foot">
          <span className="fde-foot-note">
            {isEdit ? "Changes apply to new orders only." : "Saved to this business."}
          </span>
          <button type="button" className="rf-btn rf-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="rf-btn rf-btn-primary"
            onClick={save}
            disabled={saving || nameMissing || pctInvalid}
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add director"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* A place field storing the legacy JSON string. Typing keeps just the
   name; picking a suggestion attaches coordinates, which is what turns
   the address into a map link on the card and a pin on a run sheet. */
function PlacesField({ label, placeholder, value, onChange }) {
  const loc = locOf(value);
  const text = loc.name || "";
  const pinned = Boolean(loc.coordinates);

  const handleSelect = (address) => {
    geocodeByAddress(address)
      .then((results) => getLatLng(results[0]))
      .then((coordinates) => {
        onChange(JSON.stringify({ name: address, coordinates }));
      })
      .catch(() => {
        onChange(JSON.stringify({ name: address }));
      });
  };

  return (
    <div className="rf-field fde-places">
      <span className="rf-label">
        {label}
        {pinned && (
          <span className="fde-pinned">
            <MdPlace /> pinned
          </span>
        )}
      </span>
      <PlacesAutocomplete
        value={text}
        onChange={(next) => onChange(JSON.stringify({ name: next }))}
        onSelect={handleSelect}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <>
            <span className="rf-input-shell">
              <input {...getInputProps({ placeholder })} />
            </span>
            {(loading || suggestions.length > 0) && (
              <div className="fde-suggests">
                {loading && <div className="fde-suggest-loading">Searching…</div>}
                {suggestions.map((s) => {
                  const { key, ...rest } = getSuggestionItemProps(s, {
                    className: `fde-suggest${s.active ? " is-active" : ""}`,
                  });
                  return (
                    <div key={s.placeId || s.description} {...rest}>
                      {s.description}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </PlacesAutocomplete>
    </div>
  );
}

export default FuneralDirectorsRedesign;
